import './css/Editor.css';
import './css/Header.css';

import * as React from 'react';
import { DragDropContext } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import TouchBackend from 'react-dnd-touch-backend';
import MultiBackend, { TouchTransition } from 'react-dnd-multi-backend';
import { connect } from 'react-redux';
import { detect } from 'detect-browser';
import { getHomePageRoute } from '../../infrastructure/HomePageRoute'
const browser = detect();

import {
  dragNDropProduct,
  moveCrop,
  onPageOrSpreadSelect,
  removeProduct,
  saveDocument,
  updateBrandings,
} from '../../actions/actions';
import { placeOrder } from '../../actions/orderActions';
import {
  IBlank,
  ITechSheet,
  IBranding,
  ICropDivider,
  IFillerImage,
  ISeedCategory,
  ISeedDivider,
  ICrop,
  IDocument,
  IPage,
  ISpread,
  IUserData,
  ProductCategory,
  ISeedProduct,
  IShelfDivider,
  ITechReferenceSheet,
  IOrderQuantities_Addresses,
  ITagline,
} from '../../infrastructure/interfaces';
import { productType } from '../../infrastructure/ProductType';
import { TooltipCont } from '../../infrastructure/tooltips';
import { TabTitles } from '../../infrastructure/TabTitles';
import { PageType } from '../../infrastructure/PageType';
import { Tab, Tabs } from '../Tabs';
import CropPageView from './components/CropPageView/CropPageView';
import ResultAside from './components/Result/ResultAside';
import Settings from './components/Settings';
import ImageItem from './components/SidePanel/ImagesTab/ImageItem';
import ObjectTab from './components/SidePanel/ObjectTab/ObjectTab';
import PagesTab from './components/SidePanel/PagesTab/PagesTab';
import ProductTab from './components/SidePanel/ProductTab/ProductTab';
import RightSidePanel from './components/SidePanel/RightSidePanel/RightSidePanel';
import { LayoutType } from 'src/infrastructure/LayoutTypes';

export const TabButton = (props: any) => {
  return (
    <div>
      <div className="tabsLogoBox">
        <img src={props.img} className="tabsLogoImg" />
      </div>
      {props.text}
    </div>
  );
};

const mapDispatchToProps = (dispatch: any): any => {
  return {
    saveDocument: (document: IDocument, userID: string, redirect?: string) => {
      dispatch(saveDocument(document, userID, redirect));
    },
    placeOrder: (document: IDocument, userID: string, user_email: string, username: string, comments: string, orderQuantitiesAddresses: IOrderQuantities_Addresses[]) => {
      dispatch(placeOrder(document, userID, user_email, username, comments, orderQuantitiesAddresses));
    },
    moveCrop: (dragIndex: number, hoverIndex: number) => {
      dispatch(moveCrop(dragIndex, hoverIndex));
    },
    updateBrandings: (brandings: any) => {
      dispatch(updateBrandings(brandings));
    },
    dragNDropProduct: (data: any) => {
      dispatch(dragNDropProduct(data));
    },
    removeProduct: (data: any) => {
      dispatch(removeProduct(data));
    },
    onSpreadSelect: (spread: ISpread): void => {
      dispatch(onPageOrSpreadSelect(spread));
    },
    onPageSelect: (page: IPage): void => {
      dispatch(onPageOrSpreadSelect(page));
    },
  };
};

const mapStateToProps = (state: any, ownProps: any): any => {
  return {
    productCategories: state.wizard.productCategories,
    pages: state.document.pages,
    spreads: state.document.spreads,
    document: state.document,
    crops: state.wizard.crops,
    brandings: state.wizard.brandings,
    disclaimers: state.wizard.disclaimers,
    fillerImages: state.wizard.fillerImages,
    userData: state.user.data,
    editor: state.editor,
    dividers: state.wizard.dividers,
    agronomyReferenceSheetsPublished: state.wizard.agronomyReferenceSheetsPublished,
    blanksPublished: state.wizard.blanksPublished,
    techSheets: state.wizard.techSheetsPublished,
    seedCategories: state.wizard.seedCategories,
    seedDividers: state.wizard.seedDividers,
    cropDividers: state.wizard.cropDividers,
    isSaved: state.editor.isSaved,
    seedList: state.seedList,
    taglines: state.wizard.taglines,
  };
};

export interface IEditorProps {
  saveDocument(document: IDocument, userID: string, redirect?: string): void;
  placeOrder(document: IDocument, userID: string, username: string, user_email: string, comments: string, orderQuantitiesAddresses: IOrderQuantities_Addresses[]): void;
  moveCrop(dragIndex: number, hoverIndex: number): void;
  dragNDropProduct(data: any): void;
  removeProduct(data: any): void;
  onSpreadSelect(spread: ISpread): void;
  onPageSelect(page: IPage): void;
  updateBrandings: any;
  agronomyReferenceSheetsPublished: ITechReferenceSheet[];
  brandings: IBranding[];
  dividers: IShelfDivider[];
  techSheets: ITechSheet[];
  blanksPublished: IBlank[];
  crops: ICrop[];
  cropDividers: ICropDivider[];
  seedCategories: ISeedCategory[];
  seedDividers: ISeedDivider[];
  document: IDocument;
  editor: any;
  fillerImages: IFillerImage[];
  productCategories: ProductCategory[];
  disclaimers: any[];
  userData: IUserData;
  isSaved: boolean;
  seedList: ISeedProduct[];
  taglines: ITagline[];
}

export interface IEditorState {
  droppedBoxNames: any[];
  currentTab: number;
  isActiveName: boolean;
  documentName: string;
  selectedPage?: IPage;
  selectedSpread?: ISpread;
}

const HTML5toTouch = {
  backends: [
    {
      backend: HTML5Backend,
    },
    {
      backend: TouchBackend({
        enableMouseEvents: true,
        ignoreContextMenu: true,
        delayTouchStart: 200,
      }),
      preview: true,
      transition: TouchTransition,
    },
  ],
};

const touchBackend = {
  backend: TouchBackend({
    enableMouseEvents: true,
    ignoreContextMenu: true,
    delayTouchStart: 200,
  }),
};

@DragDropContext(browser && browser.name === 'edge' ? touchBackend.backend : MultiBackend(HTML5toTouch))
@(connect(mapStateToProps, mapDispatchToProps) as any)
export default class Editor extends React.Component<IEditorProps, IEditorState>{
  input: HTMLInputElement;
  firstChangeName: boolean;
  autoSaveTimer: any;

  constructor(props: any) {
    super(props);
    const { document, editor } = props;
    const selectedSpread = (document && document.spreads) ? document.spreads[0] : undefined;
    const selectedPage = (selectedSpread) ? (selectedSpread.right || selectedSpread.left) : undefined;
    editor.selectedSpread = editor.selectedSpread || selectedSpread;
    editor.selectedPage = editor.selectedPage || selectedPage;
    this.firstChangeName = true;
    this.state = {
      droppedBoxNames: [],
      currentTab: (props.document.type === productType.Bulletin || props.document.type === productType.Poster) ? 1 : 0, // Bulletins and Poster skip the pages tab
      isActiveName: false,
      documentName: document.name,
      selectedPage,
      selectedSpread,
    };
  }

  autoSave = () => {
    if (!this.props.isSaved) {
      const filteredCrops: ICrop[] = this.props.document.crops.filter(documentCrop => this.props.crops.includes(documentCrop));
      this.props.saveDocument({...this.props.document, crops: filteredCrops}, this.props.userData.id);
    }
  }

  onGoHome = (history: any) => {
    this.props.saveDocument(this.props.document, this.props.userData.id);
    const homePageRoute = getHomePageRoute(this.props.userData.role);
    history.push(homePageRoute);
  }
  componentDidMount() {
    this.autoSaveTimer = setInterval(this.autoSave, 5000);
    addEventListener('contextmenu', (e) => {
      if (window.innerWidth < 1024) {
        e.preventDefault();
      }
    });
  }

  componentWillUnmount() {
    clearInterval(this.autoSaveTimer);
  }

  componentWillReceiveProps(nextProps: any) {
    const newSelectedSpread = nextProps.editor.selectedSpread || this.props.editor.selectedSpread;
    const newSelectedPage = nextProps.editor.selectedPage || this.props.editor.selectedPage;
    const selectedSpread = (this.props.editor.selectedSpread) ? this.props.editor.selectedSpread.id : undefined;
    const selectedPage = (this.props.editor.selectedPage) ? this.props.editor.selectedPage.number : undefined;

    const resetSelectedSpread = (newSelectedSpread
      && (newSelectedSpread.id !== selectedSpread)
      && (newSelectedSpread.left.type === 1));

    const resetSelectedPage = (newSelectedPage
      && (newSelectedPage.number !== selectedPage)
      && (newSelectedPage.type === 1 || newSelectedPage.type === 0));

    if (resetSelectedSpread || resetSelectedPage) {
        this.setState({ currentTab: 0 });
    }

    if (this.state.documentName !== nextProps.document.name) {
      this.setState({ documentName: nextProps.document.name });
    }
  }

  onSpreadSelect = (spread: ISpread) => {
    this.props.onSpreadSelect(spread);
    this.setState({ selectedSpread: spread, selectedPage: (spread.right != null) ? spread.right : spread.left });
  }

  onPageSelect = (page: IPage) => {
    const selectedSpread = this.props.document.spreads
      .find(s => s.left.number === page.number || ((s.right != null) && (s.right.number === page.number)));
    this.props.onPageSelect(page);
    this.setState({ selectedSpread, selectedPage: page });
  }

  dragNDropProduct = (data: any) => {
    this.props.dragNDropProduct(data);

    this.setState({
      selectedSpread: this.props.editor.selectedSpread,
      selectedPage: this.props.editor.selectedPage,
    });
  }
  removeProduct = (data: any) => {
    this.props.removeProduct(data);

    this.setState({
      selectedSpread: this.props.editor.selectedSpread,
      selectedPage: this.props.editor.selectedPage,
    });
  }

  checkCoverView() {
    const spread = this.props.editor.selectedSpread;
    //Postcard front page is of type "Front"
    if(this.props.document.type === "Postcard") {
      return (spread != undefined) && (spread.left != undefined) && (spread.left.type != undefined) && (spread.left.type !== 0);
    }
    return (spread != undefined) && (spread.left != undefined) && (spread.left.type != undefined) && (spread.left.type !== 2);
  }

  disableCatalogImageTab(){
    return (this.props.editor.selectedPage.layout !== LayoutType.MultiLayout)
        && (this.props.editor.selectedPage.layout !== LayoutType.Agronomy);
  }

  saveName(name: string) {
    const document = { ...this.props.document, name };

    this.props.saveDocument(document, this.props.userData.id);
  }

  pageChanged = (page: IPage) => {
    if (page.number === 1 || page.number === 4) {
      this.setState({ currentTab: 0 });
    }
    this.onPageSelect(page);
  }

  spreadChanged = (spread: ISpread) => {
    if (spread.left.title === 'Back Cover' && ((spread.right != null) && (spread.right.title === 'Front Cover'))) {
      this.setState({ currentTab: 0 });
    }
    this.onSpreadSelect(spread);
  }

  tabChanged = (tab: number) => {
    this.setState({ currentTab: tab });
  }

  headerContent = () => {
    const saveName = () => {
      this.firstChangeName = false;
      if (this.props.document.name !== this.state.documentName) {
        this.saveName(this.state.documentName);
      }
      this.setState({ isActiveName: false });
    };

    return (
      this.state.currentTab !== 3 ?
        <div className="change-view">
          <div className="change-view-name">
            {
              this.state.isActiveName ?
                <input
                  type="text"
                  disabled={!this.state.isActiveName}
                  ref={ref => this.input = ref!}
                  className="change-view-name-input"
                  maxLength={35}
                  value={this.state.documentName}
                  onBlur={() => saveName()}
                  onChange={(event) => {
                    this.setState({ documentName: event.target.value });
                  }}
                /> :
                <span
                  className="change-view-name-input"
                  title={`${TooltipCont.DOCUMENT_NAME} ${this.props.document.type}`}
                >
                  {
                    this.firstChangeName && new RegExp(`^My New ${this.props.document.type}$`, '').test(this.state.documentName) ?
                      'Edit Document Name Here' :
                      this.state.documentName
                  }
                </span>
            }

            <img src={require('../Editor/images/pencil.svg')} className="img-pencil"
              title={TooltipCont.PENCIL}
              onClick={() => {
                this.setState({ isActiveName: true }, () => this.input.focus());
              }} />
          </div>
        </div> :
        <div></div>
    );
  }
  buildPagesTab = () => {
    let tabs;
    switch (this.props.document.type) {
      case productType.Brochure:
        {
          tabs = (<PagesTab
            selectedSpread={this.props.editor.selectedSpread}
            selectedPage={this.props.editor.selectedPage}
            pages={this.props.document.pages}
            spreads={this.props.document.spreads}
            selectPage={this.onPageSelect}
            selectSpread={this.onSpreadSelect}
            productType={this.props.document.type}
          />);
          break;
        }
      case productType.Postcard:
        {
          tabs = (<PagesTab
            selectedSpread={this.props.editor.selectedSpread}
            selectedPage={this.props.editor.selectedPage}
            pages={this.props.document.pages}
            spreads={this.props.document.spreads}
            selectPage={this.onPageSelect}
            selectSpread={this.onSpreadSelect}
            productType={this.props.document.type}
          />);
          break;
        }
      case productType.Catalog:
        {
          tabs = (<ResultAside isEditorView={true} isSpreadView={true} document={null} disclaimers={null} />);
          break;
        }
      case productType.Bulletin:
        {
          tabs = (<ResultAside isEditorView={true} isSpreadView={false} document={null} disclaimers={null} />);
          break;
        }
      case productType.Poster:
        {
          tabs = (<ResultAside isEditorView={true} isSpreadView={true} document={null} disclaimers={null} />);
          break;
        }
      default:
        break;
    }
    return tabs;
  }
  render() {
    let pagesTabTitle = null;

    if ((this.props.editor.selectedPage)
      && (this.props.editor.selectedPage.type === PageType.Front || this.props.editor.selectedPage.type === PageType.Back)) {
      if (this.props.document.type === productType.Brochure) {
        pagesTabTitle = TabTitles.FRONT_BACK_COVER_BROCHURE;
      } else if (this.props.document.type === productType.Catalog) {
        pagesTabTitle = TabTitles.FRONT_BACK_COVER_CATALOG;
      } else if (this.props.document.type === productType.Postcard) {
        pagesTabTitle = (this.props.editor.selectedPage.type === PageType.Back)
        ? TabTitles.FRONT_BACK_POSTCARD
        : TabTitles.PAGES;
      }
    } else {
      pagesTabTitle = TabTitles.PAGES;
    }
    // Bulletins and Posters don't have a pages tab. Just one page.
    let pagesMarkup = (this.props.document.type !== productType.Bulletin) && (this.props.document.type !== productType.Poster) ?
      (<Tab
            title={<TabButton img={require('./images/1.svg')} text="Pages" />}
            className="content"
          >
            {this.buildPagesTab()}
            <div className="product-container">
              <div className="product-container__title">
                <span>
                  {pagesTabTitle}
                </span>
              </div>
              <CropPageView
                dragNDropProduct={this.dragNDropProduct}
                removeProduct={this.removeProduct}
              />
            </div>
            <RightSidePanel
              docType={this.props.document.type}
              userData={this.props.userData}
              showPageNavButtons={this.props.document.type !== productType.Postcard}
             />
          </Tab>
    ) : [];

    return (
      <div className="editor">
        <Tabs
          activeTabClassName="selectedButton"
          tabClassName="tabHeaderButton"
          tabHeaderClassName="header"
          tabsContainerClassName="headerTabContainer"
          onTabChanged={this.tabChanged}
          currentTab={this.state.currentTab}
          renderToHeader={this.headerContent}
        >
          {pagesMarkup}
          <Tab
            title={<TabButton img={require('./images/2.svg')} text="Products" />}
            disabled={this.checkCoverView()}
            className="content"
          >
            <ProductTab
              productCategories={this.props.productCategories}
              seedList={this.props.seedList}
            />
            <div className="product-container">
              <div className="product-container__title">
                <span>{this.props.document.type === productType.Bulletin
                  ? TabTitles.PRODUCT_FOR_SINGLE_PLACEHOLDER
                  : TabTitles.PRODUCTS}</span></div>
              <CropPageView
                dragNDropProduct={this.dragNDropProduct}
                removeProduct={this.removeProduct}
              />
            </div>
            <RightSidePanel
              docType={this.props.document.type}
              userData={this.props.userData}
              showPageNavButtons={this.props.document.type !== productType.Postcard && this.props.document.type !== productType.Poster}
            />
          </Tab>
          {
            this.props.document.type === productType.Brochure || this.props.document.type === productType.Postcard || this.props.document.type === productType.Poster ?
              <Tab
                title={<TabButton img={require('./images/3.svg')} text="Images" />}
                className="content"
                disabled={this.checkCoverView()}
              >
                <div className={`sidePane`}>
                  <div className="imagesContainer">
                    {
                      this.props.fillerImages.map(img => (
                        <ImageItem key={img.id}
                          image={img.src}
                          type="item"
                        />
                      ))
                    }
                  </div>
                  <img className="sidePane-logo" src={require('./components/SidePanel/img/logo.svg')} />
                </div>
                <div className="product-container">
                  <div className="product-container__title"><span>{TabTitles.IMAGES}</span></div>
                  <CropPageView
                    dragNDropProduct={this.dragNDropProduct}
                    removeProduct={this.removeProduct}
                  />
                </div>
                <RightSidePanel
                  docType={this.props.document.type}
                  userData={this.props.userData}
                  showPageNavButtons={this.props.document.type !== productType.Postcard && this.props.document.type !== productType.Poster}
                />
              </Tab> :
            (this.props.document.type === productType.Catalog || this.props.document.type === productType.Bulletin) ?
            <Tab
              title={<TabButton img={require('./images/3.svg')} text="Extras" />}
              disabled={this.checkCoverView()}
              className="content"
            >
              <ObjectTab
                  docType ={this.props.document.type}
                  shelfDividers={this.props.dividers}
                  techSheets={this.props.techSheets}
                  agronomyReferenceSheetsPublished={this.props.agronomyReferenceSheetsPublished}
                  blanksPublished={this.props.blanksPublished}
                  cropDividers={this.props.cropDividers}
                  seedCategories = {this.props.seedCategories}
                  seedDividers={this.props.seedDividers}
                  pages={this.props.document.pages}
              />
              <div className="product-container">
                <div className="product-container__title"><span>{TabTitles.OBJECTS}</span></div>
                <CropPageView
                  dragNDropProduct={this.dragNDropProduct}
                  removeProduct={this.removeProduct}
                />
              </div>
              <RightSidePanel
                docType={this.props.document.type}
                userData={this.props.userData}
                showPageNavButtons={this.props.document.type !== productType.Postcard && this.props.document.type !== productType.Poster}
              />
            </Tab>
            : ''
          }
          {this.props.document.type === productType.Catalog ? 
            <Tab
              title={<TabButton img={require('./images/3.svg')} text="Images" />}
              className="content"
              disabled={this.disableCatalogImageTab()}
              >
              <div className={`sidePane`}>
                <div className="imagesContainer">
                  {
                    this.props.fillerImages.map(img => (
                      <ImageItem key={img.id}
                        image={img.src}
                        type="item"
                      />
                    ))
                  }
                </div>
                <img className="sidePane-logo" src={require('./components/SidePanel/img/logo.svg')} />
              </div>
              <div className="product-container">
                <div className="product-container__title"><span>{TabTitles.IMAGES}</span></div>
                <CropPageView
                  dragNDropProduct={this.dragNDropProduct}
                  removeProduct={this.removeProduct}
                />
              </div>
              <RightSidePanel
                docType={this.props.document.type}
                userData={this.props.userData}
                showPageNavButtons={this.props.document.type !== productType.Postcard && this.props.document.type !== productType.Poster}
              />
            </Tab>
          : '' }
          <Tab
            title={<TabButton img={require('./images/4.svg')} text="Options" />}
            className="content"
          >
            <Settings
              userData={this.props.userData}
              taglines={this.props.taglines}
            />
          </Tab>
          <Tab
            title={<TabButton img={require('./images/5.svg')} text="Home" />}
            onClick={this.onGoHome}
            className="content homeHeaderBtn">
          </Tab>
        </Tabs>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/Editor/Editor.tsx